import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { FormControlLabelController } from '@components/controllers/FormControlLabelController';
import { TextFieldController } from '@components/controllers/TextFieldController';
import { StepProps } from '@features/reports2/types';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Checkbox, Typography } from '@mui/material';
import { FC, Suspense, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { CreateReportFieldValues } from '../../validation';
import { InnerPaper, StepContentBox, StepTitle } from '../common';
import { ReportSummary } from './ReportSummary';
import { getHoursAndMinutes } from '@features/reports2/utils/schedule';

const SuspenseComponent: FC<StepProps> = (props) => {
  const { setValue, ...methods } = useFormContext<CreateReportFieldValues>();
  const { _ } = useLingui();

  const errors = methods.formState.errors.fifthStep;
  const onFinishEnabled = !errors || Object.values(errors).length === 0;

  const scheduleEnabled = methods.watch('fourthStep.enabled');
  const checkedSave = methods.watch('fifthStep.saveTemplate');

  useEffect(() => {
    if (scheduleEnabled) {
      setValue('fifthStep.saveTemplate', true);
    }
  }, [scheduleEnabled, setValue]);

  const values = methods.watch();

  const isEditing = props.actions.isEditing;

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        onFinishEnabled: props.actions.onFinishEnabled && onFinishEnabled,
        onFinish: () => {
          methods.trigger().then((success) => {
            if (success) {
              props.actions.onFinish();
            }
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Resumen general</Trans>}
        description={<Trans>Visualiza el resumen de los parámetros antes configurados</Trans>}
      />

      <InnerPaper>
        <ReportSummary
          deviceIds={values.firstStep.devices}
          metrics={values.firstStep.metrics}
          timeParameters={{
            period: values.secondStep.period,
            meanWindow: values.secondStep.meanWindow,
            timezone: values.secondStep.timezone,
          }}
          format={{
            createPdf: values.thirdStep.createPdf,
            createExcel: values.thirdStep.createExcel,
            emails: values.thirdStep.emails.map(({ email }) => email),
          }}
          enabledSchedule={values.fourthStep.enabled}
          schedule={
            values.fourthStep.schedule
              ? {
                  ...values.fourthStep.schedule,
                  ...getHoursAndMinutes(values.fourthStep.schedule.time),
                }
              : undefined
          }
        />
      </InnerPaper>

      <InnerPaper mt={4}>
        <Typography fontWeight="bold" mb={2}>
          {isEditing ? (
            <Trans>Modificar detalles del guardado de reporte</Trans>
          ) : (
            <Trans>Guardar como reporte recurrente</Trans>
          )}
        </Typography>

        {!isEditing && (
          <FormControlLabelController
            disabled={scheduleEnabled || isEditing}
            control={methods.control}
            name="fifthStep.saveTemplate"
            inputControl={<Checkbox />}
            label={_(msg`¿Deseas guardar este reporte para poder ejecutarlo en otro momento?`)}
          />
        )}

        {checkedSave && (
          <>
            <TextFieldController
              control={methods.control}
              name="fifthStep.templateName"
              label={_(msg`Especifica el nombre con el que se guardará la plantilla`)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  sx: {
                    display: 'block',
                    mt: 2,
                    maxWidth: 500,
                  },
                },
              }}
            />
            <TextFieldController
              control={methods.control}
              name="fifthStep.templateDescription"
              label={_(msg`Descripción`)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  multiline: true,
                  minRows: 3,
                  maxRows: 10,
                  sx: {
                    display: 'block',
                    mt: 2,
                    maxWidth: 500,
                  },
                },
              }}
            />
          </>
        )}
      </InnerPaper>
    </StepContentBox>
  );
};

export const SummaryStep: FC<StepProps> = (props) => {
  return (
    <ApolloErrorBoundary>
      <Suspense>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
